// Single source of truth for per-route SEO metadata (Japan-first).
//
// Consumed by BOTH:
//   - the runtime <Seo> component (src/components/Seo.tsx) — updates the live
//     <head> on client-side navigation, for Googlebot's JS render + browser tab.
//   - the build-time injector (scripts/prerender-seo.mjs, runs as `postbuild`) —
//     bakes the same meta into build/<route>/index.html so non-JS crawlers
//     (LINE / X / Facebook / Slack link previews) get correct og tags, and
//     generates sitemap.xml from this same list.
//
// Kept as .mjs (not .ts) so the plain-Node build script and the TS app can both
// import it without a compile step. All copy is Japanese-native (not machine
// translated) — the canonical indexed language for pitto.jp is Japanese.

// Extension is mandatory: webpack treats .mjs as strict ESM (fullySpecified).
import { TOOL_LIST } from '../pages/portal/tools/toolsList.mjs';

export const SITE = {
  baseUrl: 'https://www.pitto.jp',
  siteName: 'Pitto',
  defaultOgImage: '/og-image.png',
  twitterCard: 'summary_large_image',
  locale: 'ja_JP',
  lang: 'ja',
  companyJa: '河津桜ソフト株式会社',
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Pitto',
  legalName: '河津桜ソフト株式会社',
  url: 'https://www.pitto.jp',
  logo: 'https://www.pitto.jp/logo512.png',
  description:
    'ファイルやURLからAIカスタマーサポート・チャットボットを構築できるSaaS。専門的なAI知識やコードは不要。',
};

const softwareAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Pitto',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description:
    'ドキュメントをアップロードするかURLを入力するだけで、社内FAQやカスタマーサポートに自動応答するAIチャットボットを構築。Webウィジェット・LINE公式アカウント・APIに対応。',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'JPY',
    description: '無料プランあり',
  },
};

// FAQ shown (and structured) on /try. Kept truthful to the product so the
// FAQPage rich result and the on-page copy stay in sync.
export const TRIAL_FAQ = [
  {
    q: 'PittoのAIチャットボットは無料で試せますか？',
    a: 'はい。登録不要で、ファイルをアップロードするかURLを入力するだけで、30秒ほどでAIチャットを無料でお試しいただけます。',
  },
  {
    q: 'AIやプログラミングの知識は必要ですか？',
    a: '必要ありません。専門的なAI知識やコードは一切不要で、資料を追加するだけでAIが自動的に回答できるようになります。',
  },
  {
    q: 'どんなデータを読み込めますか？',
    a: 'テキストの貼り付け、.txt / .md / .pdf ファイルのアップロード、WebページURLの取り込みに対応しています。',
  },
  {
    q: '社内FAQやカスタマーサポートに使えますか？',
    a: 'はい。作成したナレッジベースはWebウィジェットやLINE公式アカウント、APIから利用でき、社内FAQや顧客対応の自動化に活用できます。',
  },
];

const faqPageJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

const breadcrumb = (name, path) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'ドキュメント', item: 'https://www.pitto.jp/docs' },
    { '@type': 'ListItem', position: 2, name, item: `https://www.pitto.jp${path}` },
  ],
});

// 使い方 steps, one group per tool shell (TOOL_LIST[].how selects the group).
// MIRROR of portal.tools.how<How>1..3 in locales/ja-JP.json — the shells render
// those i18n keys in three languages; these ja copies feed the HowTo structured
// data, which is Japanese-only like the rest of this file. scripts/prerender-seo.mjs
// asserts the two stay identical, so drift fails the build instead of shipping.
export const HOW_STEPS = {
  Write: [
    '対象のテキストを入力欄に貼り付けます',
    '必要に応じて宛先や場面などのヒントを追加します',
    'ボタンを押し、生成された結果をコピーして利用します',
  ],
  Url: [
    '対象ページのURLを入力します',
    '実行ボタンを押して解析が終わるのを待ちます',
    '結果を確認し、必要に応じてサイトの修正・活用に役立てます',
  ],
  Pdf: [
    'PDFファイルをドラッグ＆ドロップまたはクリックで選択します',
    '実行ボタンを押して処理が終わるのを待ちます',
    '結果をコピーまたはダウンロードして利用します',
  ],
  Faq: [
    'テキストを貼り付けるか、WebページのURLを指定します',
    '生成ボタンを押すと、AIがFAQ（質問と回答）を作成します',
    '結果をMarkdownでコピーして、サイトや資料に活用します',
  ],
  Roi: [
    '月間の問い合わせ件数・対応時間・時給を入力します',
    'AIによる自動化率を想定に合わせて調整します',
    '削減時間とコスト削減額を確認し、導入判断の参考にします',
  ],
};

const TOOLS_INDEX_URL = 'https://www.pitto.jp/tools';

const softwareAppTool = (name, description, path) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name,
  url: `${SITE.baseUrl}${path}`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'JPY', description: '無料・登録不要' },
});

const howToJsonLd = (name, steps) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: `${name}の使い方`,
  step: steps.map((text, i) => ({ '@type': 'HowToStep', position: i + 1, text })),
});

const toolBreadcrumb = (name, path) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE.baseUrl}/` },
    { '@type': 'ListItem', position: 2, name: '無料ツール', item: TOOLS_INDEX_URL },
    { '@type': 'ListItem', position: 3, name, item: `${SITE.baseUrl}${path}` },
  ],
});

// changefreq/priority feed sitemap.xml. noindex pages are excluded there.
export const PAGES = [
  {
    path: '/',
    title: 'Pitto｜ノーコードで作るAIカスタマーサポート・チャットボット',
    description:
      'ファイルをアップロードするかURLを入力するだけ。専門的なAI知識やコードは不要で、社内FAQやカスタマーサポートに自動応答するAIチャットボットを構築。Webウィジェット・LINE公式アカウント・APIに対応。まずは無料でお試しください。',
    priority: '1.0',
    changefreq: 'weekly',
    jsonLd: [orgJsonLd, softwareAppJsonLd],
  },
  {
    path: '/try',
    title: 'ファイルやURLだけで作るAIチャットボット｜Pittoを30秒で無料お試し',
    description:
      '専門的なAI知識やコードは一切不要。ドキュメントをアップロードするか、URLを入力するだけで、社内FAQ・カスタマーサポートに答えるAIチャットが完成します。登録不要、今すぐ無料でお試しください。',
    priority: '1.0',
    changefreq: 'weekly',
    jsonLd: [faqPageJsonLd(TRIAL_FAQ)],
  },
  {
    path: '/pricing',
    title: '料金プラン｜Pitto AIチャットボット（無料プランあり）',
    description:
      'Pittoの料金プラン。無料で始められ、利用量に応じてスターター・プロへ。トークン従量課金でムダなく、AIカスタマーサポートを小さく始めて拡張できます。',
    priority: '0.9',
    changefreq: 'weekly',
  },
  {
    path: '/features',
    title: '機能紹介｜AIネイティブのカスタマーサポート「Pitto」（シナリオ設計・意図選択不要）',
    description:
      'シナリオ分岐や意図選択に頼る従来型チャットボットとは別世代。Pittoは自然文を理解して出典つきの回答を生成するAIネイティブ設計。サイト丸ごと学習・LINE連携・有人対応・MCP／APIでAIエージェント時代にも対応します。',
    priority: '0.9',
    changefreq: 'monthly',
  },
  {
    path: '/contact',
    title: 'お問い合わせ｜Pitto',
    description:
      'Pittoへのお問い合わせ・導入のご相談。AIカスタマーサポートの導入や、料金・機能に関するご質問はこちらから。',
    priority: '0.6',
    changefreq: 'yearly',
  },
  {
    path: '/mcp',
    title: 'MCPサーバー連携｜Pitto（Claudeカスタムコネクタ対応）',
    description:
      'PittoのナレッジベースをMCP（Model Context Protocol）経由でClaude等のAIアシスタントから直接検索・質問。OAuth対応のリモートMCPサーバーの使い方を解説します。',
    priority: '0.7',
    changefreq: 'monthly',
  },
  {
    path: '/news',
    title: 'お知らせ・ニュース｜Pitto',
    description: 'Pittoの新機能・アップデート・お知らせの一覧です。',
    priority: '0.5',
    changefreq: 'weekly',
  },
  {
    path: '/kumamoto',
    title: '熊本・九州の復興を支援｜AI問い合わせ窓口「Pitto」無償提供',
    description:
      '令和8年熊本地震で被災された自治体・公共施設・観光施設・企業の皆さまへ。AI問い合わせ窓口「Pitto」の全機能を、熊本県内は2年間、九州の他6県は1年間、無償で提供します。費用は一切かからず、無償期間終了後の自動課金もありません。要求に応じて初期設定は弊社も代行可能です。',
    priority: '0.9',
    changefreq: 'weekly',
  },
  // ---- 無料ツール（獲得チャネル：docs/MARKET_STRATEGY_2026-08.md Phase A1）。
  // ターゲットKWは検証済みの空白ワード：FAQ 自動生成 AI 無料 / チャットボット
  // 費用対効果 / ウェブサイト クロール ツール 無料。
  {
    path: '/tools',
    title: '無料ツール｜FAQ自動生成・チャットボットROI計算・サイト診断｜Pitto',
    description:
      'AIカスタマーサポート導入に役立つ無料オンラインツール集。FAQ自動生成AI、チャットボット費用対効果シミュレーター、Webサイトのクロールプレビューを登録不要でご利用いただけます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/keigo-converter',
    tool: '敬語変換AI',
    title: '敬語変換AI（無料）｜文章を正しい敬語・ビジネス文に書き換え',
    description:
      'カジュアルな文章を貼り付けるだけで、AIが正しい敬語の自然なビジネス文に無料で書き換え。言い換え・丁寧語への変換に。登録不要、お客様への返信文面づくりにそのまま使えます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/apology-mail',
    tool: '謝罪文・お詫びメール作成AI',
    title: '謝罪文・お詫びメール作成AI（無料）｜状況を入力するだけで文面を自動作成',
    description:
      '状況を入力するだけで、AIがビジネス慣習に沿った謝罪文・お詫びメールの文面を無料で自動作成。件名から結びまで完全な例文を生成、登録不要でコピーしてすぐ使えます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/inquiry-reply',
    tool: '問い合わせ返信メール作成AI',
    title: '問い合わせ返信メール作成AI（無料）｜カスタマー対応の返信文面を自動作成',
    description:
      'お客様からの問い合わせ内容を貼り付けるだけで、AIが丁寧な返信メールの文面を無料で自動作成。カスタマーサポート・ビジネスメールの返信例文づくりに。登録不要。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/proofreader',
    tool: '文章校正AI',
    title: '文章校正AI（無料）｜誤字脱字・敬語の誤りをチェックして自動修正',
    description:
      'ビジネス文章を貼り付けるだけで、AIが誤字脱字・敬語の誤用・不自然な表現を無料でチェックし修正案を提示。修正点の解説つき、登録不要でお使いいただけます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/ogp-checker',
    tool: 'OGP確認・チェッカー',
    title: 'OGP確認・チェッカー（無料）｜SNSシェア時の表示プレビューとメタタグ診断',
    description:
      'URLを入力するだけで、X（Twitter）・Facebook・LINEでシェアされた時の表示を無料でプレビュー。OGP・メタタグ・Twitterカードの設定漏れをチェックし、不足項目を一覧で診断します。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/link-checker',
    tool: 'リンク切れチェックツール',
    title: 'リンク切れチェックツール（無料）｜サイト内のリンク切れを一括チェック',
    description:
      'URLを入力するだけで、サイトマップとページ内リンクからサイト内ページの生存を一括チェック。リンク切れ（404等）を無料で検出します。登録不要・ブラウザ完結のリンクチェッカーです。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/sitemap-generator',
    tool: 'XMLサイトマップ作成',
    title: 'XMLサイトマップ作成ツール（無料）｜URLを入力するだけで自動生成',
    description:
      'URLを入力するだけでサイトをクロールし、検索エンジン向けのXMLサイトマップ（sitemap.xml）を無料で自動生成。コピーまたはダウンロードしてそのまま使えます。登録不要。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/faq-generator',
    tool: 'FAQ自動生成AI',
    title: 'FAQ自動生成AI（無料）｜テキストやURLからよくある質問を自動作成',
    description:
      'テキストを貼り付けるか、WebページのURLを入力するだけで、AIがFAQ（よくある質問と回答）を無料で自動生成。登録不要、Markdown形式でコピーしてそのまま使えます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/chatbot-roi',
    tool: 'チャットボット費用対効果シミュレーター',
    title: 'チャットボット費用対効果シミュレーター（無料）｜削減時間とROIを計算',
    description:
      '月間問い合わせ件数・対応時間・時給を入力するだけで、AIチャットボット導入による削減時間・削減金額・投資回収率（ROI）を無料で試算できるシミュレーターです。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/crawl-preview',
    tool: 'サイトクロールプレビュー',
    title: 'サイトクロールプレビュー（無料）｜Webサイトの読み込み可能ページを一覧表示',
    description:
      'URLを入力するだけで、サイトマップとページ内リンクからWebサイトの取り込み可能なページ一覧を無料でチェック。AIチャットボットの学習範囲を事前に確認できます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  // ---- PDF×AI ツール（Phase A1.7）。方針：特定サービス名は出さず
  // 「PDFと対話できるAI」等のカテゴリ語で訴求する。
  {
    path: '/tools/pdf-summarizer',
    tool: 'PDF要約AI',
    title: 'PDF要約AI（無料・登録不要）｜アップロードするだけで概要と要点を自動生成',
    description:
      'PDFをアップロードするだけで、AIが本文を解析して全体概要と要点を無料で自動生成。レポート・マニュアル・論文の内容を数十秒で把握。登録不要、ファイルは処理後すぐに破棄されます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/pdf-to-text',
    tool: 'PDFテキスト抽出',
    title: 'PDFテキスト抽出（無料）｜PDFから本文テキストを取り出してコピー・ダウンロード',
    description:
      'PDFをアップロードするだけで本文テキストを無料で抽出。見出し構造の復元とヘッダー・フッター除去つきで、コピーしてそのまま使える整ったテキストに。登録不要・ブラウザ完結。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/tools/pdf-to-markdown',
    tool: 'PDF→Markdown変換',
    title: 'PDF Markdown変換（無料）｜見出し構造を保ったままPDFをMarkdown化',
    description:
      'PDFの目次から見出し構造を復元し、Markdown形式に無料で変換。生成AIへの入力・ドキュメント管理・静的サイトの原稿づくりに。登録不要でコピー・ダウンロードできます。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  // ---- 比較・機能ページ（Phase A2）。方針：特定他社名は出さず、方式・
  // 傾向レベルで一般化して比較する（名誉毀損・信用毀損リスク回避。真実で
  // あっても公益目的が認められない商業比較は免責されない）。FAQ は各ページ
  // の表示内容（portal.compare.* の faqs）と同期させること。
  {
    path: '/compare/overseas-ai-chatbot',
    title: '海外製AIチャットボットと日本製の違い｜日本語対応・円建て・LINE連携で比較',
    description:
      'サイトを学習するAIチャットボットは海外製が先行。日本語UI・日本語サポート・日本円請求・LINE公式アカウント連携・出典表示が必要な方向けに、日本製Pittoとの一般的な違いを比較します。',
    priority: '0.8',
    changefreq: 'monthly',
    jsonLd: [
      faqPageJsonLd([
        {
          q: '海外製と日本製で、機能に大きな差はありますか？',
          a: '「サイトやファイルを学習してAIが自動回答する」という中核機能の方式は共通です。差が出やすいのは日本市場対応（日本語UI・サポート、日本円請求、LINE連携）と、回答に出典を表示できるかどうかです。',
        },
        {
          q: '海外製ツールからの乗り換えは大変ですか？',
          a: '同じ「WebサイトURL・ファイルを学習させる」方式であれば、同じデータソースを指定するだけで移行できます。Pittoは登録不要の無料お試しがあり、乗り換え前に回答品質を比較できます。',
        },
        {
          q: 'LINE公式アカウントに対応していますか？',
          a: 'Pittoは標準対応です。海外製ツールはLINE連携を提供していないことが多いため、LINEでの自動応答が必要な場合は対応状況をご確認ください。',
        },
      ]),
    ],
  },
  {
    path: '/line-ai-chatbot',
    title: 'LINE公式アカウントにAIチャットボットを導入｜Q&A登録不要・サイト学習型のPitto',
    description:
      'LINE公式アカウントに、自社サイトや資料を丸ごと学習したAIチャットボットを接続。Q&Aを一件ずつ登録しなくても、内容に基づいて出典つきで自動回答します。WebサイトとLINEで同じ回答。月額2,900円から。',
    priority: '0.8',
    changefreq: 'monthly',
    jsonLd: [
      faqPageJsonLd([
        {
          q: 'PittoをLINE公式アカウントに接続するには？',
          a: 'LINE Messaging APIのチャネルを作成し、PittoのダッシュボードでチャネルID等を設定するだけです。WebサイトとLINEで同じナレッジベースが回答します。',
        },
        {
          q: 'Q&A登録型と生成型（Pitto）の違いは何ですか？',
          a: 'Q&A登録型は、あらかじめ登録した質問と回答のセットから該当するものを提示する方式で、登録していない聞き方には答えられないのが一般的です。Pittoはサイトや資料を丸ごと学習し、質問ごとにAIが文章を生成して出典を添えるため、登録作業なしで幅広い質問に対応できます。',
        },
        {
          q: 'LINEとWebサイトで回答は同じになりますか？',
          a: '同じナレッジベースを参照するため、どちらのチャネルでも同じ内容に基づいて回答します。',
        },
      ]),
    ],
  },
  {
    path: '/compare/enterprise-chatbot',
    title: '商談不要のAIチャットボット｜エンタープライズ型（商談型）との違いと選び方',
    description:
      '企業向けチャットボットは要問い合わせ・商談・構築支援つきが主流。料金公開（Free〜月額2,900円）・登録して即日開始・URL入力で丸ごと学習のセルフサーブ型Pittoとの違いと、規模別の選び方を解説します。',
    priority: '0.8',
    changefreq: 'monthly',
    jsonLd: [
      faqPageJsonLd([
        {
          q: 'エンタープライズ型チャットボットの料金相場は？',
          a: '料金非公開（要問い合わせ）の製品が多く、初期費用＋月額の定額制が一般的です。事前に予算を把握したい場合は、料金を公開しているセルフサーブ型が検討しやすくなります。',
        },
        {
          q: '商談型とセルフサーブ型、どちらが向いていますか？',
          a: '専任チームの構築代行や社内システム連携が必要な大規模導入は商談型、予算とスピードを重視する中小規模はセルフサーブ型が向いています。Pittoは登録不要の無料お試しで自社サイトへの回答精度を先に確認できます。',
        },
        {
          q: 'Pittoは大規模利用にも対応できますか？',
          a: '上位プランで対応しています。まずは無料で回答品質をご確認のうえ、要件が大きい場合はお問い合わせください。',
        },
      ]),
    ],
  },
  {
    path: '/docs',
    title: 'ドキュメント｜Pitto 開発者ガイド',
    description:
      'Pittoの使い方ガイド。ナレッジベース作成、Webウィジェット設置、LINE連携、Query API、MCP、Webhookなどを解説します。',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/docs/quickstart',
    title: 'クイックスタート｜Pitto ドキュメント',
    description: '5分でPittoを始める。ナレッジベースを作成し、AIチャットを試すまでの手順を解説します。',
    priority: '0.7',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('クイックスタート', '/docs/quickstart')],
  },
  {
    path: '/docs/knowledge-bases',
    title: 'ナレッジベース｜Pitto ドキュメント',
    description: 'ドキュメントのアップロード、URL取り込み、エントリー管理など、ナレッジベースの使い方。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('ナレッジベース', '/docs/knowledge-bases')],
  },
  {
    path: '/docs/widget',
    title: 'Webウィジェット設置｜Pitto ドキュメント',
    description: '数行のコードでWebサイトにAIチャットウィジェットを埋め込む方法を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('Webウィジェット', '/docs/widget')],
  },
  {
    path: '/docs/widget-appearance',
    title: 'チャットウィジェットの見た目をカスタマイズ｜Pitto ドキュメント',
    description: 'チャットの色・表示位置・アイコン画像・ヘッダー名・入力欄の文言、そしてカスタムCSSを管理画面から変更する方法を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('ウィジェットの外観', '/docs/widget-appearance')],
  },
  {
    path: '/docs/messages',
    title: 'チャット内の文言を変更する｜Pitto ドキュメント',
    description: '挨拶文、回答が見つからないときの返信、「人間のエージェントをリクエストできます」の一文——それぞれの出どころと変更・削除の方法を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('チャットの文言', '/docs/messages')],
  },
  {
    path: '/docs/channels',
    title: 'チャネル連携（LINE）｜Pitto ドキュメント',
    description: 'LINE公式アカウントなど、外部チャネルとPittoを連携する設定手順を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('チャネル連携', '/docs/channels')],
  },
  {
    path: '/docs/conversations',
    title: '会話管理｜Pitto ドキュメント',
    description: '会話ログの確認、有人対応への引き継ぎ、未回答質問の管理について解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('会話管理', '/docs/conversations')],
  },
  {
    path: '/docs/contacts',
    title: '問い合わせ・リード管理｜Pitto ドキュメント',
    description: '会話から取得した連絡先・リード情報の管理方法を解説します。',
    priority: '0.5',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('問い合わせ・リード', '/docs/contacts')],
  },
  {
    path: '/docs/query-api',
    title: 'Query API｜Pitto ドキュメント',
    description: 'REST／SSEでナレッジベースに質問するQuery APIの使い方を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('Query API', '/docs/query-api')],
  },
  {
    path: '/docs/mcp',
    title: 'MCP連携｜Pitto ドキュメント',
    description: 'MCPサーバー経由でAIアシスタントからナレッジベースを利用する方法を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('MCP連携', '/docs/mcp')],
  },
  {
    path: '/docs/webhooks',
    title: 'Webhook｜Pitto ドキュメント',
    description: '会話やイベントを外部システムに通知するWebhookの設定方法を解説します。',
    priority: '0.5',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('Webhook', '/docs/webhooks')],
  },
  {
    path: '/docs/billing',
    title: '料金・課金｜Pitto ドキュメント',
    description: 'プラン、トークン従量課金、請求に関する仕組みを解説します。',
    priority: '0.5',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('料金・課金', '/docs/billing')],
  },
  {
    path: '/docs/security',
    title: 'セキュリティ｜Pitto ドキュメント',
    description: 'データの取り扱いとセキュリティに関する情報を解説します。',
    priority: '0.5',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('セキュリティ', '/docs/security')],
  },
  {
    path: '/docs/faq',
    title: 'よくある質問（FAQ）｜Pitto ドキュメント',
    description: 'Pittoの導入・機能・料金に関する、よくある質問と回答をまとめました。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [faqPageJsonLd(TRIAL_FAQ)],
  },
  // ---- 無料ツールの解説カテゴリ（ツールページ本体は使い方のみ、解説はここ）。
  {
    path: '/docs/tools-writing',
    title: 'ビジネス文章AIツールの解説｜敬語変換・お詫びメール・文章校正・問い合わせ返信',
    description:
      '無料のビジネス文章AIツール（敬語変換・謝罪文作成・文章校正・問い合わせ返信）の仕組みと活用方法を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('ビジネス文章AIツール', '/docs/tools-writing')],
  },
  {
    path: '/docs/tools-pdf',
    title: 'PDFツールの解説｜PDF要約AI・テキスト抽出・Markdown変換',
    description:
      '無料のPDFツール（AI要約・テキスト抽出・Markdown変換）の仕組みと、PDF資料をAIで活用する方法を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('PDFツール', '/docs/tools-pdf')],
  },
  {
    path: '/docs/tools-site',
    title: 'サイト診断ツールの解説｜OGP確認・リンク切れチェック・サイトマップ生成',
    description:
      '無料のサイト診断ツール（OGPチェッカー・リンク切れチェック・XMLサイトマップ生成・クロールプレビュー）の仕組みと使い方を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('サイト診断ツール', '/docs/tools-site')],
  },
  {
    path: '/docs/tools-chatbot',
    title: 'チャットボット導入ツールの解説｜FAQ自動生成・費用対効果シミュレーター',
    description:
      'FAQ自動生成AIとチャットボット費用対効果シミュレーターの使い方、AIチャットボット導入検討への活かし方を解説します。',
    priority: '0.6',
    changefreq: 'monthly',
    jsonLd: [breadcrumb('チャットボット導入ツール', '/docs/tools-chatbot')],
  },
  {
    path: '/privacy',
    title: 'プライバシーポリシー｜Pitto',
    description: 'Pittoのプライバシーポリシー。個人情報の取り扱いについて記載しています。',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/terms',
    title: '利用規約｜Pitto',
    description: 'Pittoサービスの利用規約です。',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/tokushoho',
    title: '特定商取引法に基づく表記｜Pitto',
    description: '特定商取引法に基づく表記を掲載しています。',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/cookies',
    title: 'Cookieポリシー｜Pitto',
    description: 'PittoのCookieの利用に関するポリシーです。',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/dpa',
    title: 'データ処理契約（DPA）｜Pitto',
    description: 'Pittoのデータ処理契約（DPA）に関する情報です。',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/aup',
    title: '利用ポリシー（AUP）｜Pitto',
    description: 'Pittoの許容利用ポリシー（AUP）に関する情報です。',
    priority: '0.3',
    changefreq: 'yearly',
  },
];

// Structured data for the 13 free-tool pages, generated rather than written out
// 13× by hand. It has to live here (not in prerender-seo.mjs) because Seo.tsx
// wipes every ld+json on client-side render and re-emits only page.jsonLd — a
// block injected by the build script alone would vanish for JS-rendering bots.
const toolByPath = new Map(TOOL_LIST.map((t) => [t.path, t]));
for (const page of PAGES) {
  const tool = page.tool && toolByPath.get(page.path);
  if (!tool) continue;
  page.jsonLd = [
    ...(page.jsonLd || []),
    softwareAppTool(page.tool, page.description, page.path),
    howToJsonLd(page.tool, HOW_STEPS[tool.how]),
    toolBreadcrumb(page.tool, page.path),
  ];
}

// The tools index: breadcrumb + the list of tools it links to.
const toolsIndex = PAGES.find((p) => p.path === '/tools');
toolsIndex.jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE.baseUrl}/` },
      { '@type': 'ListItem', position: 2, name: '無料ツール', item: TOOLS_INDEX_URL },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '無料ツール一覧',
    itemListElement: TOOL_LIST.map((tool, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: PAGES.find((p) => p.path === tool.path).tool,
      url: `${SITE.baseUrl}${tool.path}`,
    })),
  },
];

// Normalise a pathname to match a PAGES entry (strip trailing slash except root).
export function getPage(pathname) {
  let p = (pathname || '/').split('?')[0].split('#')[0];
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return PAGES.find((entry) => entry.path === p) || null;
}
